import React, {
  useState,
  ReactElement,
  useEffect,
  useMemo,
  useCallback,
  useReducer,
} from 'react';
import { Entity } from '../../types/entities/Entity';
import { Schema } from '../../types/Schema';
import {
  Button as MUIButton,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useStateContext } from '../../hooks/useStateContext';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';
import moment from 'moment';
import 'react-tabs/style/react-tabs.css';
import DialogContent from '@mui/material/DialogContent';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import SendIcon from '@mui/icons-material/Send';
import { SlidePaneSkeleton } from '../Loading/SlidePaneSkeleton';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { LoadingButton } from '@mui/lab';
import ActivityServiceStructureDialog from '../Table/layout/ActivityServiceStructureDialog';
import { DailyTransferSchedule } from '../ActivityReservations/DailyTransferSchedule';

type Props<T extends Entity> = {
  schema: Schema<T> | Schema<any>;
  mainSchema?: Schema<T> | Schema<any>;
  id: number | undefined | string;
  onSubmit: (item: Partial<T>) => void;
  result?: (item: Partial<T>) => void;
  onClose: (item: any) => void;
  properties?: Partial<T>;
  propertiesFromTable: any;
  row?: any;
  hasSlidingPane?: boolean;
  stepper?: boolean;
};

const reducer = (state: any, action: any, schema?: any) => {
  switch (action.type) {
    case 'SET_INPUT':
      const newCurrent = {
        ...state.current,
        [action.payload.name]: action.payload.value,
      };
      const newHistory = [
        ...state.history.slice(0, state.pointer + 1),
        newCurrent,
      ];

      if (action.payload.name === 'unit_price') {
        newCurrent['unit_price_gross'] = parseFloat(
          (action.payload.value * (state.current['vat'] / 100 + 1)).toFixed(2)
        );
      }
      if (action.payload.name === 'unit_price_gross') {
        newCurrent['unit_price'] = parseFloat(
          (action.payload.value / (1 + state.current['vat'] / 100)).toFixed(2)
        );
      }

      if (action.payload.name === 'vat') {
        if (newCurrent?.unit_price) {
          newCurrent['unit_price_gross'] = parseFloat(
            (newCurrent?.unit_price * (action.payload.value / 100 + 1)).toFixed(
              2
            )
          );
        }
        if (newCurrent?.unit_price_gross) {
          newCurrent['unit_price'] = parseFloat(
            (
              newCurrent?.unit_price_gross /
              (1 + action.payload.value / 100)
            ).toFixed(2)
          );
        }
      }

      if (action.payload.name === 'group_price' && action.payload.split) {
        newCurrent[`prices`] = {};
        for (let i = 1; i < 51; i++) {
          const price = parseFloat((action.payload?.value / i).toFixed(2));
          newCurrent[`prices`][`size_${i}`] = price;
        }
      }

      if (action.payload.name === 'total_price') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['sales_price_person_night'] =
            action.payload.value / nights / size;
        }
      }

      if (action.payload.name === 'sales_price_person_night') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['total_price'] = action.payload.value * nights * size;
        }
      }

      if (action.payload.name === 'total_cost') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['gross_cost_person_night'] =
            action.payload.value / nights / size;
        }
      }

      if (action.payload.name === 'gross_cost_person_night') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['total_cost'] = action.payload.value * nights * size;
        }
      }
      if (
        action?.schema &&
        (action?.schema?.name === 'Services' ||
          action?.schema?.name === 'Activity') &&
        action.payload.name === 'name'
      ) {
        newCurrent['sp_name'] = action.payload.value;
      }

      return {
        ...state,
        history: newHistory,
        current: newCurrent,
        pointer: state.pointer + 1,
        lastChanged: action.payload.name,
      };
    case 'SET_DEFAULT':
      const newCurrentDefault = action.payload ? action.payload : {};

      const newHistoryDefault = [...state.history, newCurrentDefault];
      return {
        ...state,
        history: newHistoryDefault,
        current: newCurrentDefault,
        lastChanged: undefined,
      };
    case 'UNDO':
      if (state.pointer > 0) {
        const prevPointer = state.pointer - 1;
        return {
          ...state,
          current: state.history[prevPointer],
          pointer: prevPointer,
        };
      }
      return state;
    case 'REDO':
      if (state.pointer < state.history.length - 1) {
        const nextPointer = state.pointer + 1;
        return {
          ...state,
          current: state.history[nextPointer],
          pointer: nextPointer,
        };
      }
      return state;
    default:
      return state;
  }
};

const countOpenDialogs = () => {
  return document.querySelectorAll('.MuiDialog-root').length;
};

export const SlidingPaneAddNew = <T extends Entity>({
  schema,
  mainSchema,
  onSubmit: handleSubmit,
  id,
  result,
  propertiesFromTable,
  row,
  onClose,
  hasSlidingPane,
  stepper,
}: Props<T>): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [softLoading, setSoftLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [
    openActivityServiceStructureDialog,
    setOpenActivityServiceStructureDialog,
  ] = useState(false);
  const [
    openActivityServiceStructure,
    setOpenActivityServiceStructure,
  ] = useState(false);
  const [edited, setEdited] = useState(false);
  const { user } = useStateContext();
  const [requiredFieldsFromDatabase, setRequiredFieldsFromDatabase] = useState<
    any
  >([]);
  const [state, dispatch] = useReducer(reducer, {
    history: [],
    current: {},
    pointer: -1,
  });
  const [missingFields, setMissingFields] = useState<any>([]);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: !countOpenDialogs() ? 0 : 35 * countOpenDialogs(),
    y: !countOpenDialogs() ? 0 : -20 * countOpenDialogs(),
  });

  const [confirmPriceSplit, setConfirmPriceSplit] = useState({
    open: false,
    data: {},
  });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [forcedValue, setForcedValue] = useState<any>({});
  const draggablePaneId = useMemo(() => {
    return `draggable-responsive-add-new-dialog-${uuidv4()}`;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleDrag = (e: any, data: any) => {
    setPosition({ x: data.x, y: data.y });
  };

  const getMissingFields = useCallback(async () => {
    const requiredFieldFromView = schema?.viewTable
      ? await api
          .get(`/required-fields/${user.id}/${schema?.viewTable}`)
          .then((res: any) => {
            setRequiredFieldsFromDatabase(res);
            return res;
          })
      : [];

    const columnKeys: any = schema?.columns
      ? (Object.keys(schema.columns) as Array<keyof T>)
      : [];

    const requiredFields = columnKeys.filter((columnKey: string) => {
      const column: any = schema.columns[columnKey as keyof {}];

      const required = requiredFieldFromView[`${columnKey}`];

      if (schema?.viewTable) {
        if (column.required && !column.readOnly) {
          return true;
        }
        return required;
      }

      return column.required && !column.readOnly;
    });

    return requiredFields.filter(
      (requiredField: any) =>
        state.current[requiredField] === undefined ||
        state.current[requiredField] === null ||
        state.current[requiredField] === ''
    );
  }, [state]);

  const PaperComponent = useCallback((props: PaperProps) => {
    return (
      <Draggable
        handle={`#${draggablePaneId}`}
        cancel={'[class*="MuiDialogContent-root"], .non-draggable-button'}
      >
        <Paper {...props} />
      </Draggable>
    );
  }, []);

  const onSubmit = useCallback(async () => {
    setSoftLoading(true);

    try {
      const missingFields: any = await getMissingFields();
      setMissingFields(missingFields);

      if (!missingFields.length) {
        if (schemaHasClient(schema)) {
          const result: any = await schema.client.create({
            ...state.current,
            group: propertiesFromTable?.group || state.current?.group,
            schema: mainSchema,
          });

          if (result?.messages){

            result?.messages.map((message:any)=>{
              toast(message, {
                icon: '⏳',
              });
            });

          }else{
            toast.success(
              `Successfully added ${schema.name} ID: ${result?.id} Name: ${result?.name}`
            );
          }

          if (
            schema?.pluralName.toLowerCase() === 'activities' &&
            state.current['images' as keyof {}]
          ) {
            const files: any = state.current['images' as keyof {}];
            const formData = new FormData();
            const salesPartnerRoles: any = [];
            const extraData: any = [];

            files.forEach((file: any, i: number) => {
              salesPartnerRoles.push({
                sales_partner_visible: file?.sales_partner_visible,
                sales_partner_use: file?.sales_partner_use,
              });
              extraData.push({
                title: file?.title,
                caption: file?.caption,
                description: file?.description,
              });
              formData.append(`file-${i}`, file);
            });

            formData.append('column', 'images');
            formData.append('id', result?.id);
            formData.append('schema', JSON.stringify(schema));
            formData.append(
              'salesPartnerRoles',
              JSON.stringify(salesPartnerRoles)
            );
            formData.append('extraData', JSON.stringify(extraData));

            try {
              await api.postFile(`/upload-files`, formData);
            } catch (error) {
              console.error('File upload error:', error);
            }
          }

          setEdited(false);
          handleSubmit(result);
        }
      } else {
        toast.error('Required field is missing');
      }
    } catch (error: any) {
      let array: string[] = [];
      for (let errorKey in error) {
        for (let errorMsg in error[errorKey]) {
          array.push(`${error[errorKey][errorMsg]}`);
        }
      }
      toast.error(array.join(','));
    } finally {
      setSoftLoading(false);
    }
  }, [getMissingFields, onClose, schema, state]);

  function handleSetChanges(name: any, value: any) {
    if (name === 'group_price') {
      setConfirmPriceSplit({
        open: true,
        data: value,
      });
    } else if (name === 'price_per_person_per_night') {

      const pricePerNight = Number(value) * Number(state?.current?.accommodation_reservation?.size);

      dispatch({
        type: 'SET_INPUT',
        payload: { name: 'price_per_person_per_night', value },
        schema: schema
      });

      dispatch({
        type: 'SET_INPUT',
        payload: { name: 'price_per_night', value: pricePerNight },
        schema: schema
      });


    } else if (name === 'price_per_night') {

      const pricePerPerson = state?.current?.accommodation_reservation?.size
        ? Number((Number(value) / Number(state?.current?.accommodation_reservation?.size)).toFixed(2))
        : 0;

      dispatch({
        type: 'SET_INPUT',
        payload: { name: 'price_per_night', value },
        schema: schema
      });

      dispatch({
        type: 'SET_INPUT',
        payload: { name: 'price_per_person_per_night', value: pricePerPerson },
        schema: schema
      });

    } else {
      dispatch({ type: 'SET_INPUT', payload: { name, value }, schema: schema });
    }
  }

  useEffect(() => {
    setLoading(true);
    const searchParams = new URLSearchParams();

    const keys = Object.keys(schema.columns).filter((column: any) => {
      if (
        column === 'a_status' ||
        column === 'b_status' ||
        column === 's_status'
      ) {
        return false;
      }
      return column.includes('status');
    });

    searchParams.append('statusFields', JSON.stringify(keys));

    if (
      schema.name === 'Activity Reservation' &&
      Object.keys(propertiesFromTable).length
    ) {
      if (propertiesFromTable?.group) {
        api
          .get(`/groups/${propertiesFromTable?.group?.id}`, searchParams)
          .then((result: any) => {
            dispatch({
              type: 'SET_DEFAULT',
              payload: {
                ['group_size']: result?.size,
                ['group']: {
                  id: result?.id,
                  name: result?.name,
                },
                ['sales_partner']: result?.sales_partner
                  ? result?.sales_partner
                  : undefined,
                ['date_added']: moment(new Date()).format('YYYY-MM-DD HH:mm'),
                ['date']: moment(new Date(result?.arrival_date)).format(
                  'YYYY-MM-DD HH:mm'
                ),
              },
            });
            setLoading(false);
          });
      } else {
        dispatch({
          type: 'SET_DEFAULT',
          payload: {
            ['date_added']: moment(new Date()).format('YYYY-MM-DD HH:mm'),
          },
        });
        setLoading(false);
      }
    } else if (
      schema.name === 'Accommodation Reservation' &&
      Object.keys(propertiesFromTable).length
    ) {
      api
        .get(`/groups/${propertiesFromTable?.group?.id}`, searchParams)
        .then((result: any) => {
          dispatch({
            type: 'SET_DEFAULT',
            payload: {
              ['reservation_status']:
                result?.activity_statuses?.reservation_status,
              ['size']: result?.size,
              ['arrival']: result?.arrival_date,
              ['departure']: result?.departure_date,
              ['sales_partner']: result?.sales_partner,
              ['group']: {
                id: result?.id,
                name: result?.name,
              },
              ['cost_currency']: {
                id: 1,
                name: 'EUR',
              },
              ['price_currency']: {
                id: 1,
                name: 'EUR',
              },
              ['cost_ifa_percent']: 4,
              ['cost_vat_percent']: 5,
            },
          });
          setLoading(false);
        });
    } else if (schema.name === 'Guide Costs') {
      api
        .get(`/guide-costs`, new URLSearchParams({ withCurrency: 'true' }))
        .then((result: any) => {
          dispatch({
            type: 'SET_DEFAULT',
            payload: {
              ['guide']: row?.guides[0] ? row?.guides[0] : null,
              ['user']: {
                id: user?.id,
                name: user?.name,
              },
              ['guide_cost_status']: result?.status ? result?.status : null,
              ['currency']: {
                id: 2,
                name: 'HUF',
              },
              ['payment_date']: moment(new Date()).format('YYYY-MM-DD'),
            },
          });
          setLoading(false);
        });
    } else if (schema.name === 'Custom Field') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: propertiesFromTable?.service_provider,
        },
      });
    } else if (schema.name === 'Single Stripper Reservation') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['activity']: row?.activity,
          ['activity_reservation']: { id: row?.id },
          ['group']: row?.group,
          ['cost_currency']: {
            id: 1,
            name: 'EUR',
          },
        },
      });
      setLoading(false);
    } else if (schema?.name === 'Service Group') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['activity']: propertiesFromTable?.activity || row,
        },
      });
      setLoading(false);
    } else if (schema.name === 'Activity Reservation Cost') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: row?.service_provider
            ? row?.service_provider
            : null,
          ['currency']: {
            id: 2,
            name: 'HUF',
          },
        },
      });
      setLoading(false);
    } else if (schema.name === 'Competitor Price') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['updated_date']: moment(new Date()).format('YYYY-MM-DD'),
        },
      });
      setLoading(false);
    } else if (schema.name === 'Service Reservations') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service']: {
            id: propertiesFromTable?.service?.id,
            name: propertiesFromTable?.service?.name,
          },
          ['currency']: {
            id: 1,
            name: 'EUR',
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Service Provider' &&
      schema.name === 'Activity Stripper Role'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
        },
      });
      setLoading(false);
    } else if (schema.name === 'Service - Event Staff') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
        },
      });
      setLoading(false);
    } else if (schema.name === 'Service Provider - Driver') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Service Provider' &&
      schema.name === 'Service Provider Venue'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
        },
      });
      setLoading(false);
    } else if (schema.name === 'Services') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
          ['currency']: {
            id: 1,
            name: 'EUR',
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Service Provider' &&
      schema.name === 'Services'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
          ['currency']: {
            id: 1,
            name: 'EUR',
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Service Provider' &&
      schema.name === 'Corporate'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Corporate' &&
      schema.name === 'Corporate'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider?.id,
            name: propertiesFromTable?.service_provider?.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Accommodation' &&
      schema.name === 'Accommodation Room'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['accommodation']: {
            id: propertiesFromTable?.accommodation?.id,
            name: propertiesFromTable?.accommodation?.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Accommodation' &&
      schema.name === 'Corporate'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['accommodation']: {
            id: propertiesFromTable?.accommodation?.id,
            name: propertiesFromTable?.accommodation?.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Service Provider' &&
      schema.name === 'Corporate Payment'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['corporate']: {
            id: propertiesFromTable?.corporate?.id,
            name: propertiesFromTable?.corporate?.name,
          },
          ['currency']: {
            id: 1,
            name: 'EUR',
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Corporate' &&
      schema.name === 'Corporate Payment'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['corporate']: {
            id: propertiesFromTable?.corporate?.id,
            name: propertiesFromTable?.corporate?.name,
          },
          ['currency']: {
            id: 1,
            name: 'EUR',
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Activity' &&
      schema.name === 'Guide Hour'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['activity']: {
            id: propertiesFromTable?.activity.id,
            name: propertiesFromTable?.activity.name,
          },
        },
      });
      setLoading(false);
    } else if (
      schema.name === 'Staff Work Time' ||
      schema.name === 'Guide Have Time' ||
      schema.name === 'Working Time'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['user']: {
            id: propertiesFromTable?.user?.id,
            name: propertiesFromTable?.user?.name,
          },
          ['from']: propertiesFromTable?.from,
          ['to']: propertiesFromTable?.to,
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Activity' &&
      schema.name === 'Activity Stripper Role'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['activity']: {
            id: propertiesFromTable?.activity.id,
            name: propertiesFromTable?.activity.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Activity' &&
      (schema.name === 'Activity Price' ||
        schema.name === 'Activity Website Price')
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['activity']: {
            id: propertiesFromTable?.activity.id,
            name: propertiesFromTable?.activity.name,
          },
          ['sales_partner']: {
            id: 40,
          },
          ['currency']: {
            id: 1,
            name: 'EUR',
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Activity' &&
      schema.name === 'Address'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['activity']: {
            id: propertiesFromTable?.activity.id,
            name: propertiesFromTable?.activity.name,
          },
        },
      });
      setLoading(false);
    } else if (schema.name === 'Group Guide') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['group']: {
            id: propertiesFromTable?.id,
            name: propertiesFromTable?.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Accommodation' &&
      schema.name === 'Accommodation Address'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['accommodation']: {
            id: propertiesFromTable?.accommodation.id,
            name: propertiesFromTable?.accommodation.name,
          },
        },
      });
      setLoading(false);
    } else if (schema.name === 'Service Reservations View') {
      const viewData: any = {};
      if (propertiesFromTable?.activity_reservation) {
        viewData.activity_reservation = {
          id: propertiesFromTable?.activity_reservation?.id,
          name: propertiesFromTable?.activity_reservation?.name,
        };
      }
      if (propertiesFromTable?.group) {
        viewData.group = {
          id: propertiesFromTable?.group?.id,
          name: propertiesFromTable?.group?.name,
        };
      }

      setOpenActivityServiceStructure(true);

      dispatch({
        type: 'SET_DEFAULT',
        payload: viewData,
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Service Provider' &&
      schema.name === 'Contact'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_provider']: {
            id: propertiesFromTable?.service_provider.id,
            name: propertiesFromTable?.service_provider.name,
          },
        },
      });
      setLoading(false);
    } else if (
      mainSchema &&
      mainSchema.name === 'Guide Reservations' &&
      schema.name === 'Guide Reservations'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['guide_wage']: propertiesFromTable?.activity?.guide_wage,
          ['guide_wage_currency']:
            propertiesFromTable?.activity?.guide_wage_currency,
        },
      });
      setLoading(false);
    }
    else if (
      schema?.viewTable === 'rooms'
    ) {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['accommodation_reservation']: propertiesFromTable,
          ['accommodation_reservation_id']: propertiesFromTable?.id,
        },
      });
      setLoading(false);
    } else if (schema.name === 'Group') {
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['first_quotation_date']: moment(new Date()).format('YYYY-MM-DD'),
        },
      });
      setLoading(false);
    } else if (schema.name === 'To Do') {
      console.log(row);

      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['service_reservation_id']: row?.service_reservation_id,
          ['activity_reservation_id']: row?.activity_reservation_id,
          ['title']: `${
            row?.service ? row?.service : row?.activity_reservation
          }`,
        },
      });
      setLoading(false);
    } else {
      setLoading(false);
      dispatch({
        type: 'SET_DEFAULT',
        payload: {
          ['selling_price_currency']: {
            id: 1,
            name: 'EUR',
          },
          ['cost_currency']: {
            id: 1,
            name: 'EUR',
          },
          ['base_currency']: {
            id: 1,
            name: 'EUR',
          },
          ['price_currency']: {
            id: 1,
            name: 'EUR',
          },
          ['tourist_tax_currency']: {
            id: 1,
            name: 'EUR',
          },
          ['currency']: {
            id: 1,
            name: 'EUR',
          },
          ['guide_wage_currency']: {
            id: 2,
            name: 'HUF',
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    if (schema?.viewTable) {
      api
        .get(`/required-fields/${user.id}/${schema?.viewTable}`)
        .then((res: any) => {
          setRequiredFieldsFromDatabase(res);
        });
    }
  }, []);

  useEffect(() => {
    if (!edited && state.pointer > -1) {
      setEdited(true);
    }
    if (state.lastChanged === 'group_price' && confirmPriceSplit.open) {
      setForcedValue({ group_price: state.current.group_price });
      setConfirmPriceSplit({
        open: false,
        data: {},
      });
    }
  }, [state]);

  const splitPrice = useCallback(
    (split: boolean) => {
      const value = confirmPriceSplit.data;
      const name = 'group_price';

      dispatch({
        type: 'SET_INPUT',
        payload: { name, value, split },
        schema: schema,
      });
    },
    [state, confirmPriceSplit]
  );

  return (
    <>
      <React.Fragment>
        <Dialog
          PaperComponent={PaperComponent}
          fullScreen={fullScreen}
          open={true}
          onClose={(event) => {
            console.log('close');
          }}
          aria-labelledby="responsive-add-new-dialog"
          maxWidth={'md'}
          sx={{
            '& .MuiPaper-root': {
              width: {
                md: 'auto',
                lg: 'auto',
              },
              minWidth: {
                md: '600px',
                lg: '600px',
              },

              maxWidth: {
                md: 'calc(100vw - 400px)',
                lg: 'calc(100vw - 400px)',
              },
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '0px',
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#b3cdcae0',
            },
          }}
        >
          <DialogTitle
            id={draggablePaneId}
            sx={{
              background: '#fff',
              //color: '#fff',
              p: '6px',
              borderBottom: '1px solid #e0e0e0',
              cursor: 'move',
            }}
          >
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Stack direction={'column'} alignItems={'flex-start'}>
                <Typography variant="subtitle1">
                  Add new {schema?.name}
                </Typography>
                {schema?.description && (
                  <Typography variant="caption">
                    {schema?.description}
                  </Typography>
                )}
              </Stack>
              <Stack direction={'row'} alignItems={'center'}>
                <IconButton
                  aria-label="Close"
                  className="non-draggable-button"
                  sx={{
                    color: '#5f5f5f',
                  }}
                  onClick={() => {
                    console.log(edited);

                    if (edited) {
                      setShowConfirm(true);
                    } else {
                      onClose(false);
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: '0px',
              opacity: softLoading ? 0.5 : 1,
              pointerEvents: softLoading ? 'none' : 'auto',
            }}
          >
            <Stack direction={'row'}>
              <Stack
                sx={{
                  overflowY: 'auto',
                  maxHeight: {
                    md: 'calc(100vh - 200px)',
                  },
                  width:
                    schema.viewTable === 'activity_reservations'
                      ? '640px'
                      : '100%',
                }}
              >
                {loading ? (
                  <SlidePaneSkeleton />
                ) : (
                  <>
                    {(Object.keys(schema.columns) as Array<keyof T>).map(
                      (column) => {
                        const tabName = schema.columns[column]?.tab_name
                          ? schema.columns[column]?.tab_name
                          : 'general';

                        const withoutNameColumn =
                          schema.columns[column]?.withoutNameColumn;

                        if ('general' !== tabName) {
                          return <></>;
                        }

                        const {
                          readOnly,
                          name,
                          fieldRenderer: Field,
                          slidePaneFieldRenderer: FieldForPane,
                          slidePaneFieldRendererAddNew: FieldForPaneAddNew,
                          accordingTo,
                          nameForSidePane,
                          hide,
                          required,
                          fieldType,
                        } = schema.columns[column];
                        const isMissing = missingFields.includes(column);

                        const key = accordingTo as keyof T;
                        let accordingToHide = false;
                        if (typeof accordingTo !== 'undefined') {
                          accordingToHide = true;
                        }
                        let accordingToSendData: any = undefined;

                        if (
                          accordingToHide &&
                          schema?.name === 'Service Reservations View' &&
                          column === 'service_provider'
                        ) {
                          accordingToHide = false;
                        }

                        let readOnlyTemp = readOnly;
                        if (
                          schema?.name === 'Activity Reservation' &&
                          column === 'group'
                        ) {
                          readOnlyTemp = false;
                        }

                        if (
                          state.current[key] !== undefined &&
                          state.current[key] !== null &&
                          state.current[key] !== ''
                        ) {
                          accordingToHide = false;
                          const accordingToData: any = state.current[key];
                          accordingToSendData = {
                            name: key,
                            id: accordingToData?.id,
                            value: state.current[key],
                          };
                        }

                        if (
                          accordingToHide &&
                          schema?.viewTable === 'todos' &&
                          column === 'assigned_persons'
                        ) {
                          if (state.current?.private === true) {
                            accordingToHide = true;
                          }
                        }

                        return (
                          <>
                            <Stack
                              key={column.toString()}
                              direction="row"
                              spacing={0}
                              sx={{
                                width: '100%',
                                display: hide ? 'none' : '',
                              }}
                            >
                              {!withoutNameColumn && (
                                <Box
                                  sx={{
                                    flex: 1,
                                    borderBottom: '1px solid #dfdbdb',
                                    borderRight: '1px solid #dfdbdb',
                                    borderLeft: '1px solid #dfdbdb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: '#f1f1f1',
                                  }}
                                >
                                  <span className={'px-[8px]'}>{`${
                                    nameForSidePane ? nameForSidePane : name
                                  } ${
                                    requiredFieldsFromDatabase[column] ||
                                    required
                                      ? '*'
                                      : ''
                                  }`}</span>
                                  {isMissing && (
                                    <span style={{ color: 'red' }}>
                                      This field is missing
                                    </span>
                                  )}
                                </Box>
                              )}

                              <Box
                                sx={{
                                  flex: 2,
                                  borderBottom: '1px solid #dfdbdb',
                                  borderRight: '1px solid #dfdbdb',
                                  '& .MuiFormControl-root.MuiTextField-root': {
                                    width: '100%',
                                  },
                                  '& .DraftEditor-root': {
                                    padding: '10px',
                                    width: '100%',
                                  },
                                  maxWidth: withoutNameColumn
                                    ? '100%'
                                    : 'calc(100% - 204px)',
                                }}
                              >
                                {FieldForPaneAddNew ? (
                                  <>
                                    <FieldForPaneAddNew
                                      onChange={(value: any) => {
                                        handleSetChanges(column, value);
                                      }}
                                      defaultValue={state.current[column]}
                                      accordingToValue={accordingToSendData}
                                      isActive={
                                        !accordingToHide && !readOnlyTemp
                                      }
                                      disabled={readOnlyTemp || accordingToHide}
                                      intent={isMissing ? 'danger' : undefined}
                                      addingNewEntity={true}
                                      mui={true}
                                      column={schema.columns[column]}
                                      isAddingNew={true}
                                      allValue={state.current}
                                      mainSchema={schema}
                                      forcedValue={forcedValue[column]}
                                    />
                                  </>
                                ) : (
                                  <>
                                    {FieldForPane ? (
                                      <FieldForPane
                                        onChange={(value: any) => {
                                          handleSetChanges(column, value);
                                        }}
                                        defaultValue={state.current[column]}
                                        accordingToValue={accordingToSendData}
                                        isActive={
                                          !accordingToHide && !readOnlyTemp
                                        }
                                        disabled={
                                          readOnlyTemp || accordingToHide
                                        }
                                        intent={
                                          isMissing ? 'danger' : undefined
                                        }
                                        addingNewEntity={true}
                                        mui={true}
                                        column={schema.columns[column]}
                                        isAddingNew={true}
                                        allValue={state.current}
                                        mainSchema={schema}
                                        forcedValue={forcedValue[column]}
                                      />
                                    ) : (
                                      <></>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Stack>
                          </>
                        );
                      }
                    )}
                  </>
                )}
              </Stack>
              {schema.viewTable === 'activity_reservations' && (
                <Box
                  sx={{
                    width: '400px',
                  }}
                >
                  <DailyTransferSchedule
                    date={state.current?.date || '2025-07-01'}
                  />
                </Box>
              )}
            </Stack>
          </DialogContent>

          {loading ? (
            <>
              <Skeleton variant="rectangular" width={'100%'} height={60} />
            </>
          ) : (
            <DialogActions
              sx={{
                p: '6px',
                borderTop: '1px solid #e0e0e0',
                boxShadow: '0px -4px 10px 0px #00000017',
              }}
            >
              <Button
                variant="text"
                color={'info'}
                onClick={() => {
                  if (edited) {
                    setShowConfirm(true);
                  } else {
                    onClose(false);
                  }
                }}
              >
                Close
              </Button>

              <LoadingButton
                variant="contained"
                onClick={() => {
                  if (openActivityServiceStructure) {
                    setOpenActivityServiceStructureDialog(true);
                  } else {
                    onSubmit();
                  }
                }}
                endIcon={<SendIcon />}
                loading={softLoading}
              >
                Save
              </LoadingButton>
            </DialogActions>
          )}
        </Dialog>
      </React.Fragment>
      {openActivityServiceStructureDialog && (
        <ActivityServiceStructureDialog
          open={openActivityServiceStructureDialog}
          onClose={() => {
            setOpenActivityServiceStructureDialog(false);
          }}
          onConfirm={() => {
            onSubmit();
          }}
          data={state.current}
        />
      )}
      {showConfirm && (
        <Dialog open={true}>
          <DialogTitle>You have unsaved changes</DialogTitle>
          <DialogContent>
            <Stack direction={'row'} gap={2}>
              <Button
                variant="text"
                onClick={() => {
                  setShowConfirm(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowConfirm(false);
                  onClose(false);
                }}
                color={'error'}
              >
                Don't Save
              </Button>
              <Button variant="outlined" onClick={onSubmit}>
                Save
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      )}
      {confirmPriceSplit && (
        <Dialog
          open={confirmPriceSplit?.open}
          onClose={() => {
            setConfirmPriceSplit({
              open: false,
              data: {},
            });
          }}
        >
          <DialogTitle>{'Split the prices?'}</DialogTitle>
          <DialogActions
            sx={{
              gap: '10px',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
            }}
          >
            <MUIButton
              variant={'outlined'}
              color={'error'}
              onClick={() => {
                splitPrice(false);
              }}
              sx={{
                ml: '0px !important',
                width: {
                  xs: '100%',
                  md: '240px',
                },
              }}
            >
              No
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'primary'}
              onClick={() => {
                splitPrice(true);
              }}
              sx={{
                ml: '0px !important',
                width: {
                  xs: '100%',
                  md: '180px',
                },
              }}
              autoFocus
            >
              Yes
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

function schemaHasClient<T extends Entity>(
  schema: Schema<T>
): schema is SchemaWithClient<T> {
  return 'client' in schema;
}
